import { useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { toolInvoke, toolSuccess, toolError } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import { validateFileSize, mergePdfs, mergedFilename, MAX_FILE_MB } from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

const UTILITY = getUtilityDefinition('mergePdf').analyticsKey

// ─── State machine ────────────────────────────────────────────────────────────

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; files: File[] }
  | { kind: 'loading'; files: File[] }
  | { kind: 'success'; files: File[]; blob: Blob; objectUrl: string; totalPages: number }
  | { kind: 'error'; files: File[]; message: string; errorCode: string }

// ─── Drop-zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFiles: (files: FileList) => void
  disabled: boolean
}

function DropZone({ onFiles, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.mergePdf.pickLabel}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled && e.dataTransfer.files.length > 0) onFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.mergePdf.dragPdfs} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        multiple
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onFiles(e.target.files)
        }}
        disabled={disabled}
      />
    </div>
  )
}

// ─── File list item ───────────────────────────────────────────────────────────

interface FileListItemProps {
  file: File
  index: number
  total: number
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
  disabled: boolean
}

function FileListItem({
  file,
  index,
  total,
  onMoveUp,
  onMoveDown,
  onRemove,
  disabled,
}: FileListItemProps) {
  const formatBytes = useFormatBytes()
  const t = useT()

  return (
    <div className="flex items-center gap-2 rounded-lg border bg-accent/30 px-3 py-2.5">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded bg-primary/10 text-xs font-medium text-primary">
        {index + 1}
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <div className="flex shrink-0 gap-0.5">
        <button
          type="button"
          onClick={onMoveUp}
          disabled={disabled || index === 0}
          aria-label={t.mergePdf.moveUp}
          className={cn(
            'rounded p-1 text-muted-foreground transition-colors',
            'hover:bg-accent hover:text-foreground',
            (disabled || index === 0) && 'pointer-events-none opacity-30',
          )}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onMoveDown}
          disabled={disabled || index === total - 1}
          aria-label={t.mergePdf.moveDown}
          className={cn(
            'rounded p-1 text-muted-foreground transition-colors',
            'hover:bg-accent hover:text-foreground',
            (disabled || index === total - 1) && 'pointer-events-none opacity-30',
          )}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={t.common.deleteFile}
          className={cn(
            'rounded p-1 text-muted-foreground transition-colors',
            'hover:bg-destructive/10 hover:text-destructive',
            disabled && 'pointer-events-none opacity-50',
          )}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// ─── File list ────────────────────────────────────────────────────────────────

interface FileListProps {
  files: File[]
  onReorder: (files: File[]) => void
  onRemove: (index: number) => void
  disabled: boolean
}

function FileList({ files, onReorder, onRemove, disabled }: FileListProps) {
  const t = useT()

  function moveItem(from: number, to: number) {
    if (to < 0 || to >= files.length) return
    const next = [...files]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved!)
    onReorder(next)
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-foreground">
        {t.mergePdf.filesLabel} ({files.length})
      </p>
      <div className="space-y-1.5">
        {files.map((file, i) => (
          <FileListItem
            key={`${file.name}-${file.size}-${i}`}
            file={file}
            index={i}
            total={files.length}
            onMoveUp={() => moveItem(i, i - 1)}
            onMoveDown={() => moveItem(i, i + 1)}
            onRemove={() => onRemove(i)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  )
}

// ─── Result panel ─────────────────────────────────────────────────────────────

interface ResultPanelProps {
  files: File[]
  blob: Blob
  objectUrl: string
  totalPages: number
  onReset: () => void
}

function ResultPanel({ files, blob, objectUrl, totalPages, onReset }: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = mergedFilename(files.map((f) => f.name))
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.mergePdf.mergedFiles}</span>
            <span className="font-medium text-foreground">{files.length}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.mergePdf.totalPages}</span>
            <span className="font-medium text-foreground">{totalPages}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(blob.size)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {t.common.download}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {t.mergePdf.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Error panel ──────────────────────────────────────────────────────────────

interface ErrorPanelProps {
  message: string
  onRetry: () => void
}

function ErrorPanel({ message, onRetry }: ErrorPanelProps) {
  const t = useT()
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="15" x2="12.01" y2="15" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

// ─── Main form ────────────────────────────────────────────────────────────────

export function ObedinitPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })

  const isLoading = phase.kind === 'loading'

  function handleFiles(fileList: FileList) {
    const incoming = Array.from(fileList)

    // Filter only PDFs
    const pdfFiles = incoming.filter((f) => f.name.toLowerCase().endsWith('.pdf'))
    if (pdfFiles.length === 0) {
      setPhase({
        kind: 'error',
        files: phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error' || phase.kind === 'success'
          ? phase.files : [],
        errorCode: 'no_pdf',
        message: t.mergePdf.noPdf,
      })
      return
    }

    // Check file sizes
    const oversized = pdfFiles.filter((f) => !validateFileSize(f))
    if (oversized.length > 0) {
      setPhase({
        kind: 'error',
        files: phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error' || phase.kind === 'success'
          ? phase.files : pdfFiles,
        errorCode: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }

    // Clean up old success URL
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }

    const existing = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error' || phase.kind === 'success'
      ? phase.files : []
    const merged = [...existing, ...pdfFiles]
    setPhase({ kind: 'ready', files: merged })
  }

  function handleRemove(index: number) {
    const current = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error' || phase.kind === 'success'
      ? phase.files : []
    const next = current.filter((_, i) => i !== index)
    if (next.length === 0) {
      if (phase.kind === 'success') {
        URL.revokeObjectURL(phase.objectUrl)
      }
      setPhase({ kind: 'idle' })
    } else {
      setPhase({ kind: 'ready', files: next })
    }
  }

  function handleReorder(files: File[]) {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setPhase({ kind: 'ready', files })
  }

  function handleClearAll() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
    }
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error') {
      setPhase({ kind: 'ready', files: phase.files })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const files = phase.files

    setPhase({ kind: 'loading', files })
    toolInvoke(UTILITY, { file_count: files.length })

    const startMs = Date.now()

    try {
      const { blob, totalPages } = await mergePdfs(files)
      const durationMs = Date.now() - startMs
      const objectUrl = URL.createObjectURL(blob)

      toolSuccess(UTILITY, durationMs, {
        file_count: files.length,
        output_bytes: blob.size,
        total_pages: totalPages,
      })

      setPhase({
        kind: 'success',
        files,
        blob,
        objectUrl,
        totalPages,
      })
    } catch (err: unknown) {
      const code = err instanceof Error && err.message.toLowerCase().includes('encrypted')
        ? 'encrypted'
        : 'parse_error'
      const message = code === 'encrypted' ? t.mergePdf.encrypted : t.mergePdf.parseError
      toolError(UTILITY, code, { file_count: files.length })
      setPhase({ kind: 'error', files, errorCode: code, message })
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          files={phase.files}
          blob={phase.blob}
          objectUrl={phase.objectUrl}
          totalPages={phase.totalPages}
          onReset={handleClearAll}
        />
      </div>
    )
  }

  const currentFiles = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
    ? phase.files : []

  return (
    <div className="space-y-5">
      {currentFiles.length > 0 ? (
        <div className="space-y-3">
          <FileList
            files={currentFiles}
            onReorder={handleReorder}
            onRemove={handleRemove}
            disabled={isLoading}
          />
          <div className="flex gap-2">
            <DropZone onFiles={handleFiles} disabled={isLoading} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClearAll}
            disabled={isLoading}
            className="text-muted-foreground"
          >
            {t.mergePdf.clearAll}
          </Button>
        </div>
      ) : (
        <DropZone onFiles={handleFiles} disabled={isLoading} />
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading || currentFiles.length < 2}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.mergePdf.loading}
          </>
        ) : (
          t.mergePdf.submit
        )}
      </Button>

      {currentFiles.length > 0 && currentFiles.length < 2 && (
        <p className="text-center text-xs text-muted-foreground">{t.mergePdf.minFilesHint}</p>
      )}
    </div>
  )
}
